import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // envoie le cookie httpOnly de refresh vers /api/auth
});

// ── Access token conservé uniquement en mémoire (jamais dans le storage) ──────
let accessToken: string | null = null;
export const setAccessToken = (token: string | null) => { accessToken = token; };
export const getAccessToken = () => accessToken;

// Callbacks branchés par le store pour éviter les imports circulaires.
type AuthUser = Record<string, unknown>;
let onRefreshed: ((user: AuthUser, accessToken: string, expiresIn: number) => void) | null = null;
let onAuthLost: (() => void) | null = null;
export const registerAuthHandlers = (handlers: {
  onRefreshed: (user: AuthUser, accessToken: string, expiresIn: number) => void;
  onAuthLost: () => void;
}) => {
  onRefreshed = handlers.onRefreshed;
  onAuthLost = handlers.onAuthLost;
};

// ── Auth interceptor ──────────────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const url = original?.url || '';

    // Don't intercept 401 on public endpoints (OTP, inscription, etc.)
    const isPublicRoute = url.startsWith('/reinscription/') || url.startsWith('/public/') || url.startsWith('/inscription');
    // Ne pas boucler sur l'endpoint de refresh lui-même.
    const isRefreshCall = url.includes('/auth/refresh');
    if (error.response?.status === 401 && !original._retry && !isPublicRoute && !isRefreshCall) {
      original._retry = true;
      try {
        // Le refresh token voyage via le cookie httpOnly (withCredentials).
        const { data } = await api.post('/auth/refresh');
        setAccessToken(data.accessToken);
        if (data.user && onRefreshed) {
          onRefreshed(data.user, data.accessToken, data.expiresIn);
        }
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch {
        setAccessToken(null);
        if (onAuthLost) onAuthLost();
        window.location.href = '/login?expired=1';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/* ── Public inscription API ────────────────────────────────────────────────── */

export const inscriptionApi = {
  getConfig: () =>
    api.get('/public/config').then(r => r.data),

  validateLink: (token: string) =>
    api.get(`/public/validate-link/${token}`).then(r => r.data),

  submit: (payload: Record<string, unknown>) =>
    api.post('/inscription', payload).then(r => r.data),

  getStatus: (code: string) =>
    api.get(`/inscription/${code}`).then(r => r.data),

  downloadPdf: async (code: string) => {
    const res = await api.get(`/inscription/${code}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },

  sendOtp: (telephone: string) =>
    api.post('/reinscription/send-otp', { telephone }).then(r => r.data),

  verifyOtp: (telephone: string, code: string) =>
    api.post('/reinscription/verify-otp', { telephone, code }).then(r => r.data),

  selectFamily: (familyCode: string) =>
    api.post('/reinscription/select-family', { familyCode }).then(r => r.data),

  confirmPayment: (sessionId: string, codeFamille: string) =>
    api.post('/inscription/confirm-payment', { sessionId, codeFamille }).then(r => r.data),

  sendPhoneVerification: (telephone: string) =>
    api.post('/inscription/verify-phone/send', { telephone }).then(r => r.data),

  checkPhoneVerification: (telephone: string, code: string) =>
    api.post('/inscription/verify-phone/check', { telephone, code }).then(r => r.data),

  updatePaymentMode: (codeFamille: string, modePaiement: string, chequeFields?: { chequeNom?: string; chequeNumero?: string; chequeBanque?: string }) =>
    api.put(`/inscription/${codeFamille}/mode-paiement`, { modePaiement, ...chequeFields }).then(r => r.data),
};

/* ── Admin API ─────────────────────────────────────────────────────────────── */

export const adminApi = {
  getStats: () =>
    api.get('/admin/stats').then(r => r.data),

  getVerificationAnomalies: () =>
    api.get('/admin/verifications/anomalies').then(r => r.data),

  ignoreVerificationAnomaly: (data: { key: string; type: string }) =>
    api.post('/admin/verifications/ignore', data).then(r => r.data),

  getDossiers: (params?: Record<string, string>) =>
    api.get('/admin/dossiers', { params }).then(r => r.data),

  getDossier: (id: number) =>
    api.get(`/admin/dossiers/${id}`).then(r => r.data),

  getEleves: (familleId: number) =>
    api.get(`/admin/dossiers/${familleId}/eleves`).then(r => r.data),

  assignClass: (familleId: number, eleveId: number, data: { commentaire?: string; departement?: string }) =>
    api.put(`/admin/dossiers/${familleId}/eleves/${eleveId}/classe`, data).then(r => r.data),

  updateDossier: (id: number, data: Record<string, unknown>) =>
    api.put(`/admin/dossiers/${id}`, data).then(r => r.data),

  updateEleve: (familleId: number, eleveId: number, data: Record<string, unknown>) =>
    api.put(`/admin/dossiers/${familleId}/eleves/${eleveId}`, data).then(r => r.data),

  addEleve: (familleId: number, data: Record<string, unknown>) =>
    api.post(`/admin/dossiers/${familleId}/eleves`, data).then(r => r.data),

  validateDossier: (id: number) =>
    api.put(`/admin/dossiers/${id}/valider`),

  downloadPdf: async (id: number) => {
    const res = await api.get(`/admin/dossiers/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },

  createManualDossier: (payload: Record<string, unknown>) =>
    api.post('/admin/dossiers/manual', payload).then(r => r.data),

  searchFamilies: (q: string) =>
    api.get('/admin/familles/search', { params: { q } }).then(r => r.data),

  uploadScan: (dossierId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/dossiers/${dossierId}/scan`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  extractScan: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/scan/extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 min timeout for AI processing
    }).then(r => r.data);
  },

  extractCheque: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/scan/extract-cheque', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }).then(r => r.data);
  },

  getScan: (dossierId: number) =>
    api.get(`/admin/dossiers/${dossierId}/scan`, { responseType: 'blob' }).then(r => r.data as Blob),

  deleteDossier: (id: number) =>
    api.delete(`/admin/dossiers/${id}`).then(r => r.data),

  deleteEleve: (dossierId: number, eleveId: number) =>
    api.delete(`/admin/dossiers/${dossierId}/eleves/${eleveId}`).then(r => r.data),

  // Annulation d'inscription (dossier ou élève)
  annulerDossier: (id: number, data: { motif?: string; description?: string }) =>
    api.put(`/admin/dossiers/${id}/annuler`, data).then(r => r.data),

  reactiverDossier: (id: number) =>
    api.put(`/admin/dossiers/${id}/reactiver`).then(r => r.data),

  annulerEleve: (eleveId: number, data: { motif?: string; description?: string }) =>
    api.put(`/admin/eleves/${eleveId}/annuler`, data).then(r => r.data),

  reactiverEleve: (eleveId: number) =>
    api.put(`/admin/eleves/${eleveId}/reactiver`).then(r => r.data),

  getAnnulations: () =>
    api.get('/admin/annulations').then(r => r.data),

  exportExcel: () =>
    api.get('/admin/export/excel', { responseType: 'blob' }),

  // Classeur PDF a imprimer (generation asynchrone, SUPER_ADMIN)
  genererClasseur: (body: { parSite?: boolean; seuil?: number; statuts?: string[]; source?: 'INTERNET' | 'PAPIER'; placeholderPapier?: boolean }) =>
    api.post('/admin/classeur', body).then(r => r.data as { jobId: string }),

  getClasseurJob: (jobId: string) =>
    api.get(`/admin/classeur/${jobId}`).then(r => r.data as {
      statut: 'PENDING' | 'RUNNING' | 'DONE' | 'ERROR';
      traites: number;
      total: number;
      erreur: string | null;
    }),

  downloadClasseur: async (jobId: string) => {
    const res = await api.get(`/admin/classeur/${jobId}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },

  exportExcelToDrive: (googleAccessToken: string) =>
    api.post('/admin/export/excel/drive', null, {
      headers: { 'X-Google-Access-Token': googleAccessToken },
    }).then(r => r.data),

  getDriveExportFolderId: () =>
    api.get('/admin/settings/drive-export-folder-id').then(r => r.data),

  setDriveExportFolderId: (folderId: string) =>
    api.put('/admin/settings/drive-export-folder-id', { folderId }).then(r => r.data),

  // Admin user management
  getAdminUsers: () =>
    api.get('/admin/users').then(r => r.data),

  addAdminUser: (data: { email: string; name?: string; role?: string }) =>
    api.post('/admin/users', data).then(r => r.data),

  removeAdminUser: (userId: number) =>
    api.delete(`/admin/users/${userId}`).then(r => r.data),

  updateUserPermissions: (userId: number, data: { canSeeMontantTotal?: boolean; canSeeAffectations?: boolean; canSeeEleves?: boolean; canSeeEnseignants?: boolean; canSeeReferentiel?: boolean; canGenerateLinks?: boolean; canUseAiAssistant?: boolean; canSeeCalendrier?: boolean; canAnnulerInscription?: boolean; canSeeMessages?: boolean; canSeeTawassol?: boolean; canSeeDevoirs?: boolean; canSeeDashboardGlobal?: boolean; canSeeDashboardRentree?: boolean; canSeeDashboardRepartition?: boolean; canSeeDashboardAdministratif?: boolean; canSeeDashboardVieScolaire?: boolean; canSeeDashboardComptabilite?: boolean; canManageBibliotheque?: boolean; canSeeComptabilite?: boolean; canEditComptabilite?: boolean; canConfigComptabilite?: boolean; canSeeTableauBordComptabilite?: boolean; canSeeHistoriqueComptabilite?: boolean; canSeeDocumentsComptables?: boolean; canDeposerCheques?: boolean; canSeeDefraiements?: boolean; canSeeDefraiementMontants?: boolean; canSeeDefraiementDetails?: boolean; canEditClasses?: boolean; canEditAffectations?: boolean; canEditEnseignants?: boolean; canSaisirAbsences?: boolean; canSeeClassesSeances?: boolean; canGererAbsences?: boolean; canEditCalendrier?: boolean; canSeeAccueilPermanence?: boolean; canGererAlertesEleves?: boolean; canGererTaches?: boolean }) =>
    api.patch(`/admin/users/${userId}/permissions`, data).then(r => r.data),

  updateUserNotifConfig: (userId: number, data: { telephone?: string; notifAdminMsgApp?: boolean; notifAdminMsgSms?: boolean; notifAdminMsgEmail?: boolean; notifEnsMsgApp?: boolean; notifEnsMsgSms?: boolean; notifEnsMsgEmail?: boolean }) =>
    api.patch(`/admin/users/${userId}/notif-config`, data).then(r => r.data),

  // Profils de permissions
  listProfils: () =>
    api.get('/admin/profils').then(r => r.data),

  createProfil: (data: { nom: string; description?: string; permissions?: string[] }) =>
    api.post('/admin/profils', data).then(r => r.data),

  updateProfil: (id: number, data: { nom?: string; description?: string; permissions?: string[] }) =>
    api.put(`/admin/profils/${id}`, data).then(r => r.data),

  deleteProfil: (id: number) =>
    api.delete(`/admin/profils/${id}`).then(r => r.data),

  updateUserProfils: (userId: number, profilIds: number[]) =>
    api.patch(`/admin/users/${userId}/profils`, { profilIds }).then(r => r.data),

  // Comptabilite
  comptaListExercices: () =>
    api.get('/admin/comptabilite/exercices').then(r => r.data),

  comptaGetExercice: (id: number) =>
    api.get(`/admin/comptabilite/exercices/${id}`).then(r => r.data),

  comptaCreateExercice: (data: { libelle: string; dateDebut: string; dateFin: string }) =>
    api.post('/admin/comptabilite/exercices', data).then(r => r.data),

  comptaSetANouveau: (id: number, data: { soldeBanque: number; soldeCaisse: number }) =>
    api.put(`/admin/comptabilite/exercices/${id}/an`, data).then(r => r.data),

  comptaRepriseePaiements: (id: number) =>
    api.post(`/admin/comptabilite/exercices/${id}/reprise-paiements`).then(r => r.data),

  comptaListComptes: () =>
    api.get('/admin/comptabilite/comptes').then(r => r.data),

  comptaListEcritures: (id: number) =>
    api.get(`/admin/comptabilite/exercices/${id}/ecritures`).then(r => r.data),

  comptaCreateEcriture: (id: number, data: { date: string; libelle: string; journal: string; lignes: Array<{ compteNumero: string; debit: number; credit: number }> }) =>
    api.post(`/admin/comptabilite/exercices/${id}/ecritures`, data).then(r => r.data),

  comptaDeleteEcriture: (ecritureId: number) =>
    api.delete(`/admin/comptabilite/ecritures/${ecritureId}`).then(r => r.data),

  comptaUpdateEcriture: (ecritureId: number, data: { date: string; libelle: string; journal: string; lignes: Array<{ compteNumero: string; debit: number; credit: number }> }) =>
    api.put(`/admin/comptabilite/ecritures/${ecritureId}`, data).then(r => r.data),

  comptaSetExerciceActif: (exerciceId: number) =>
    api.put('/admin/comptabilite/exercices/actif', { exerciceId }).then(r => r.data),

  comptaHistoriqueEcritures: (exerciceId: number, ecritureId?: number) =>
    api.get(`/admin/comptabilite/exercices/${exerciceId}/ecritures/historique`, { params: { ecritureId } }).then(r => r.data),

  comptaAjouterJustificatifs: (ecritureId: number, fichiers: File[], source: 'LOCAL' | 'DRIVE' = 'LOCAL') => {
    const fd = new FormData();
    fichiers.forEach(f => fd.append('fichiers', f));
    return api.post(`/admin/comptabilite/ecritures/${ecritureId}/justificatifs`, fd, {
      params: { source },
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  comptaGetJustificatif: (ecritureId: number, justificatifId: number) =>
    api.get(`/admin/comptabilite/ecritures/${ecritureId}/justificatifs/${justificatifId}`, { responseType: 'blob' }).then(r => r.data as Blob),

  comptaDeleteJustificatif: (ecritureId: number, justificatifId: number) =>
    api.delete(`/admin/comptabilite/ecritures/${ecritureId}/justificatifs/${justificatifId}`).then(r => r.data),

  comptaGetDossierDriveJustificatifs: () =>
    api.get('/admin/comptabilite/parametres/drive-justificatifs').then(r => r.data),

  comptaSetDossierDriveJustificatifs: (folderId: string) =>
    api.put('/admin/comptabilite/parametres/drive-justificatifs', { folderId }).then(r => r.data),

  comptaListDriveJustificatifs: (googleAccessToken: string) =>
    api.get('/admin/comptabilite/drive/justificatifs', {
      headers: { 'X-Google-Access-Token': googleAccessToken },
    }).then(r => r.data),

  comptaCloturer: (id: number) =>
    api.post(`/admin/comptabilite/exercices/${id}/cloturer`).then(r => r.data),

  comptaRouvrir: (id: number) =>
    api.post(`/admin/comptabilite/exercices/${id}/rouvrir`).then(r => r.data),

  comptaBalance: (id: number) =>
    api.get(`/admin/comptabilite/exercices/${id}/balance`).then(r => r.data),

  comptaGrandLivre: (id: number) =>
    api.get(`/admin/comptabilite/exercices/${id}/grand-livre`).then(r => r.data),

  comptaCompteResultat: (id: number) =>
    api.get(`/admin/comptabilite/exercices/${id}/compte-resultat`).then(r => r.data),

  comptaListActivites: (id: number) =>
    api.get(`/admin/comptabilite/exercices/${id}/activites`).then(r => r.data),

  comptaGetActivite: (activiteId: number) =>
    api.get(`/admin/comptabilite/activites/${activiteId}`).then(r => r.data),

  comptaCreateActivite: (id: number, data: { date: string; libelle: string; description?: string }) =>
    api.post(`/admin/comptabilite/exercices/${id}/activites`, data).then(r => r.data),

  comptaDeleteActivite: (activiteId: number) =>
    api.delete(`/admin/comptabilite/activites/${activiteId}`).then(r => r.data),

  comptaAddMouvement: (activiteId: number, data: { date: string; libelle: string; sens: string; montant: number; tresorerie: string; compteContrepartie?: string }) =>
    api.post(`/admin/comptabilite/activites/${activiteId}/mouvements`, data).then(r => r.data),

  comptaDeleteMouvement: (activiteId: number, mvId: number) =>
    api.delete(`/admin/comptabilite/activites/${activiteId}/mouvements/${mvId}`).then(r => r.data),

  // Cheques a encaisser & bordereaux de depot
  comptaListCheques: (id: number, params?: { statut?: string; mois?: string }) =>
    api.get(`/admin/comptabilite/exercices/${id}/cheques`, { params }).then(r => r.data),

  comptaCreateCheque: (id: number, data: { date: string; emetteur?: string; banque?: string; numero?: string; montant: number; moisPrevu?: string; compteContrepartie?: string; libelle: string; scanPath?: string }) =>
    api.post(`/admin/comptabilite/exercices/${id}/cheques`, data).then(r => r.data),

  comptaDeleteCheque: (chequeId: number) =>
    api.delete(`/admin/comptabilite/cheques/${chequeId}`).then(r => r.data),

  comptaUpdateChequeMoisDepot: (chequeId: number, mois: string | null) =>
    api.put(`/admin/comptabilite/cheques/${chequeId}/mois-depot`, { mois }).then(r => r.data),

  comptaUploadChequeScan: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/comptabilite/cheques/scan', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  comptaGetChequeScan: (chequeId: number) =>
    api.get(`/admin/comptabilite/cheques/${chequeId}/scan`, { responseType: 'blob' }).then(r => r.data),

  comptaListBordereaux: (id: number) =>
    api.get(`/admin/comptabilite/exercices/${id}/bordereaux`).then(r => r.data),

  comptaCreateBordereau: (id: number, data: { date: string; banque?: string; chequeIds: number[]; mois?: string }) =>
    api.post(`/admin/comptabilite/exercices/${id}/bordereaux`, data).then(r => r.data),

  comptaGetBordereau: (bordereauId: number) =>
    api.get(`/admin/comptabilite/bordereaux/${bordereauId}`).then(r => r.data),

  comptaDeleteBordereau: (bordereauId: number) =>
    api.delete(`/admin/comptabilite/bordereaux/${bordereauId}`).then(r => r.data),

  comptaAjouterRecus: (bordereauId: number, fichiers: File[], type: 'RECU' | 'SCAN_CHEQUES' = 'RECU') => {
    const fd = new FormData();
    fichiers.forEach(f => fd.append('fichiers', f));
    return api.post(`/admin/comptabilite/bordereaux/${bordereauId}/recus`, fd, {
      params: { type },
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  comptaGetRecu: (bordereauId: number, recuId: number) =>
    api.get(`/admin/comptabilite/bordereaux/${bordereauId}/recus/${recuId}`, { responseType: 'blob' }).then(r => r.data as Blob),

  comptaDeleteRecu: (bordereauId: number, recuId: number) =>
    api.delete(`/admin/comptabilite/bordereaux/${bordereauId}/recus/${recuId}`).then(r => r.data),

  comptaVerifierBordereau: (bordereauId: number, tout = false) =>
    api.post(`/admin/comptabilite/bordereaux/${bordereauId}/verifier`, null, { params: { tout } }).then(r => r.data),

  comptaResoudreBordereau: (bordereauId: number, commentaire: string) =>
    api.post(`/admin/comptabilite/bordereaux/${bordereauId}/verification/resoudre`, { commentaire }).then(r => r.data),

  // Cloche de notifications admin
  getAdminNotifications: () =>
    api.get('/admin/notifications').then(r => r.data),

  getAdminNotificationsUnreadCount: () =>
    api.get('/admin/notifications/unread-count').then(r => r.data),

  markAdminNotificationRead: (id: number) =>
    api.put(`/admin/notifications/${id}/lu`).then(r => r.data),

  markAllAdminNotificationsRead: () =>
    api.put('/admin/notifications/lu-tout').then(r => r.data),

  recordManualPayment: (id: number, data: { modePaiement: string; montant: number; chequeNom?: string; chequeNumero?: string; chequeBanque?: string }) =>
    api.put(`/admin/dossiers/${id}/paiement-manuel`, data).then(r => r.data),

  updatePaiementAcompte: (id: number, data: { modePaiement: string; montant?: number; paye?: boolean; chequeNom?: string; chequeNumero?: string; chequeBanque?: string; chequeMoisDepot?: string }) =>
    api.put(`/admin/dossiers/${id}/paiement-acompte`, data).then(r => r.data),

  uploadScanCheque: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/dossiers/${id}/scan-cheque`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  getScanCheque: (id: number) =>
    api.get(`/admin/dossiers/${id}/scan-cheque`, { responseType: 'blob' }).then(r => r.data as Blob),

  // Paiements (additional payments)
  listPaiements: (dossierId: number) =>
    api.get(`/admin/dossiers/${dossierId}/paiements`).then(r => r.data),

  addPaiement: (dossierId: number, data: { modePaiement: string; montant: number; label?: string; paye?: boolean; chequeNom?: string; chequeNumero?: string; chequeBanque?: string; chequeMoisDepot?: string }) =>
    api.post(`/admin/dossiers/${dossierId}/paiements`, data).then(r => r.data),

  updatePaiement: (paiementId: number, data: { modePaiement?: string; montant?: number; label?: string; paye?: boolean; chequeNom?: string; chequeNumero?: string; chequeBanque?: string; chequeMoisDepot?: string }) =>
    api.put(`/admin/paiements/${paiementId}`, data).then(r => r.data),

  deletePaiement: (paiementId: number) =>
    api.delete(`/admin/paiements/${paiementId}`).then(r => r.data),

  marquerPaiementComplet: (dossierId: number) =>
    api.put(`/admin/dossiers/${dossierId}/paiement-complet`).then(r => r.data),

  genererLienPaiement: (dossierId: number, data?: { montant?: number; label?: string }) =>
    api.post(`/admin/dossiers/${dossierId}/payment-link`, data || {}).then(r => r.data),

  uploadScanChequePaiement: (paiementId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/paiements/${paiementId}/scan-cheque`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  getScanChequePaiement: (paiementId: number) =>
    api.get(`/admin/paiements/${paiementId}/scan-cheque`, { responseType: 'blob' }).then(r => r.data as Blob),

  // Communications
  getCommunications: (dossierId: number) =>
    api.get(`/admin/dossiers/${dossierId}/communications`).then(r => r.data),

  sendMessage: (dossierId: number, data: { contenu: string; sujet?: string; sendEmail?: boolean; sendSms?: boolean; reponseAutorisee?: boolean }) =>
    api.post(`/admin/dossiers/${dossierId}/send-message`, data).then(r => r.data),

  deleteCommunication: (communicationId: number) =>
    api.delete(`/admin/communications/${communicationId}`).then(r => r.data),

  // Comments
  getComments: (dossierId: number) =>
    api.get(`/admin/dossiers/${dossierId}/comments`).then(r => r.data),

  addComment: (dossierId: number, contenu: string) =>
    api.post(`/admin/dossiers/${dossierId}/comments`, { contenu }).then(r => r.data),

  deleteComment: (commentId: number) =>
    api.delete(`/admin/comments/${commentId}`).then(r => r.data),

  // Historique élève (données 2025-2026)
  getEleveHistorique: (eleveId: number) =>
    api.get(`/admin/eleves/${eleveId}/historique`).then(r => r.data),

  // Message templates
  getTemplates: () =>
    api.get('/admin/templates').then(r => r.data),

  createTemplate: (data: { nom: string; categorie?: string | null; sujet?: string; contenu: string; contenuEmail?: string | null }) =>
    api.post('/admin/templates', data).then(r => r.data),

  updateTemplate: (id: number, data: { nom: string; categorie?: string | null; sujet?: string; contenu: string; contenuEmail?: string | null }) =>
    api.put(`/admin/templates/${id}`, data).then(r => r.data),

  deleteTemplate: (id: number) =>
    api.delete(`/admin/templates/${id}`).then(r => r.data),

  // Campagnes d'envoi groupé (SMS + Email)
  getCampagnes: (params?: { page?: number; size?: number; search?: string }) =>
    api.get('/admin/campagnes', { params }).then(r => r.data),

  getCampagne: (id: number) =>
    api.get(`/admin/campagnes/${id}`).then(r => r.data),

  getCampagneMessages: (id: number, params?: { page?: number; size?: number; search?: string; statut?: string }) =>
    api.get(`/admin/campagnes/${id}/messages`, { params }).then(r => r.data),

  exportCampagne: (id: number) =>
    api.get(`/admin/campagnes/${id}/export`, { responseType: 'blob' }),

  createCampagne: (data: Record<string, unknown>) =>
    api.post('/admin/campagnes', data).then(r => r.data),

  updateCampagne: (id: number, data: Record<string, unknown>) =>
    api.put(`/admin/campagnes/${id}`, data).then(r => r.data),

  changeStatutCampagne: (id: number, statut: string) =>
    api.put(`/admin/campagnes/${id}/statut`, { statut }).then(r => r.data),

  ignorerDestinataire: (id: number, familleId: number) =>
    api.delete(`/admin/campagnes/${id}/destinataires/${familleId}`).then(r => r.data),

  envoyerCampagne: (id: number) =>
    api.post(`/admin/campagnes/${id}/envoyer`).then(r => r.data),

  renvoyerEchecsCampagne: (id: number) =>
    api.post(`/admin/campagnes/${id}/renvoyer-echecs`).then(r => r.data),

  deleteCampagne: (id: number) =>
    api.delete(`/admin/campagnes/${id}`).then(r => r.data),

  // Campagnes d'envoi groupé aux enseignants (SMS + Email + notification portail)
  getCampagnesEnseignants: (params?: { page?: number; size?: number; search?: string }) =>
    api.get('/admin/campagnes-enseignants', { params }).then(r => r.data),

  getCampagneEnseignants: (id: number) =>
    api.get(`/admin/campagnes-enseignants/${id}`).then(r => r.data),

  getCampagneEnseignantsMessages: (id: number, params?: { page?: number; size?: number; search?: string; statut?: string }) =>
    api.get(`/admin/campagnes-enseignants/${id}/messages`, { params }).then(r => r.data),

  exportCampagneEnseignants: (id: number) =>
    api.get(`/admin/campagnes-enseignants/${id}/export`, { responseType: 'blob' }),

  createCampagneEnseignants: (data: Record<string, unknown>) =>
    api.post('/admin/campagnes-enseignants', data).then(r => r.data),

  updateCampagneEnseignants: (id: number, data: Record<string, unknown>) =>
    api.put(`/admin/campagnes-enseignants/${id}`, data).then(r => r.data),

  changeStatutCampagneEnseignants: (id: number, statut: string) =>
    api.put(`/admin/campagnes-enseignants/${id}/statut`, { statut }).then(r => r.data),

  ignorerDestinataireEnseignant: (id: number, enseignantId: number) =>
    api.delete(`/admin/campagnes-enseignants/${id}/destinataires/${enseignantId}`).then(r => r.data),

  envoyerCampagneEnseignants: (id: number) =>
    api.post(`/admin/campagnes-enseignants/${id}/envoyer`).then(r => r.data),

  renvoyerEchecsCampagneEnseignants: (id: number) =>
    api.post(`/admin/campagnes-enseignants/${id}/renvoyer-echecs`).then(r => r.data),

  deleteCampagneEnseignants: (id: number) =>
    api.delete(`/admin/campagnes-enseignants/${id}`).then(r => r.data),

  // Message d'affectation unitaire (à la validation d'un dossier)
  getMessagePreview: (dossierId: number, params: { categorie?: string; dateRentree?: string; texteIntro?: string; corpsEmail?: string; sujet?: string; inclureBoutonPortail?: boolean }) =>
    api.get(`/admin/dossiers/${dossierId}/message-preview`, { params }).then(r => r.data),

  envoyerMessageDossier: (dossierId: number, data: Record<string, unknown>) =>
    api.post(`/admin/dossiers/${dossierId}/envoyer-message`, data).then(r => r.data),

  // Messages (admin views conversations)
  getMessages: (params?: { familleId?: number; page?: number; size?: number; search?: string }) =>
    api.get('/admin/messages', { params }).then(r => r.data),

  getConversationMessages: (conversationId: number) =>
    api.get(`/admin/messages/${conversationId}`).then(r => r.data),

  getBroadcastDetail: (groupId: string) =>
    api.get(`/admin/messages/broadcast/${groupId}`).then(r => r.data),

  getMessagesNonLus: () =>
    api.get('/admin/messages/non-lus').then(r => r.data),

  replyConversation: (conversationId: number, contenu: string) =>
    api.post(`/admin/messages/${conversationId}`, { contenu }).then(r => r.data),

  createConversation: (data: { familleId: number; sujet: string; contenu: string; reponseAutorisee?: boolean }) =>
    api.post('/admin/messages/nouveau', data).then(r => r.data),

  deleteConversation: (conversationId: number) =>
    api.delete(`/admin/messages/${conversationId}`).then(r => r.data),

  searchMessageFamilles: (search?: string) =>
    api.get('/admin/messages/familles', { params: search ? { search } : {} }).then(r => r.data),

  // Audit trail
  getAuditTrail: (page = 0, size = 50, search?: string) =>
    api.get('/admin/audit-trail', { params: { page, size, ...(search ? { search } : {}) } }).then(r => r.data),

  // Supervision (SUPER_ADMIN) : historique connexions + sessions actives
  getLoginHistory: (page = 0, size = 50, search?: string) =>
    api.get('/admin/security/login-history', { params: { page, size, ...(search ? { search } : {}) } }).then(r => r.data),

  getActiveSessions: () =>
    api.get('/admin/security/active-sessions').then(r => r.data),

  revokeSession: (id: number) =>
    api.delete(`/admin/security/active-sessions/${id}`).then(r => r.data),

  // Connexions portail (parents / enseignants)
  getPortailConnections: (type: 'PARENT' | 'ENSEIGNANT', page = 0, size = 20, search?: string) =>
    api.get('/admin/security/portal-connections', { params: { type, page, size, ...(search ? { search } : {}) } }).then(r => r.data),

  getPortailConnectionsSummary: () =>
    api.get('/admin/security/portal-connections/summary').then(r => r.data),

  getConnectionsWeekly: (weeks = 8) =>
    api.get('/admin/security/connections-weekly', { params: { weeks } }).then(r => r.data),

  // Evaluations
  getEvaluations: (dossierId: number) =>
    api.get(`/admin/dossiers/${dossierId}/evaluations`).then(r => r.data),

  addEvaluation: (familleId: number, eleveId: number, data: { dateEvaluation: string; typeTest: string; commentaire: string; evaluateur: string }) =>
    api.post(`/admin/dossiers/${familleId}/eleves/${eleveId}/evaluations`, data).then(r => r.data),

  updateEvaluation: (evalId: number, data: { dateEvaluation?: string; typeTest?: string; commentaire?: string; evaluateur?: string }) =>
    api.put(`/admin/evaluations/${evalId}`, data).then(r => r.data),

  deleteEvaluation: (evalId: number) =>
    api.delete(`/admin/evaluations/${evalId}`).then(r => r.data),

  // App Settings (Drive config)
  getDriveFolderId: () =>
    api.get('/admin/settings/drive-folder-id').then(r => r.data),

  setDriveFolderId: (folderId: string) =>
    api.put('/admin/settings/drive-folder-id', { folderId }).then(r => r.data),

  getDriveChequeFolderId: () =>
    api.get('/admin/settings/drive-cheque-folder-id').then(r => r.data),

  setDriveChequeFolderId: (folderId: string) =>
    api.put('/admin/settings/drive-cheque-folder-id', { folderId }).then(r => r.data),

  getSetting: (key: string) =>
    api.get(`/admin/settings/${key}`).then(r => r.data),

  setSetting: (key: string, value: string) =>
    api.put(`/admin/settings/${key}`, { value }).then(r => r.data),

  // Hifz Tracker (suivi Coran)
  getHifzConfig: () =>
    api.get('/admin/hifz/config').then(r => r.data),

  setHifzConfig: (data: { enabled?: boolean; parcoursActifs?: string[]; reciteurs?: { id: string; folder: string; label: string }[] }) =>
    api.put('/admin/hifz/config', data).then(r => r.data),

  getHifzSourates: () =>
    api.get('/admin/hifz/sourates').then(r => r.data),

  getHifzClasses: () =>
    api.get('/admin/hifz/classes').then(r => r.data),

  getHifzClasseConfig: (classeId: number) =>
    api.get(`/admin/hifz/classes/${classeId}/config`).then(r => r.data),

  setHifzClasseConfig: (classeId: number, data: { actif?: boolean; touteSourates?: boolean; sourateNumeros?: number[] }) =>
    api.put(`/admin/hifz/classes/${classeId}/config`, data).then(r => r.data),

  // Hifz Tracker : suivi de progression (lecture seule)
  getHifzSuiviClasses: () =>
    api.get('/admin/hifz/suivi/classes').then(r => r.data),

  getHifzSuiviClasse: (classeId: number) =>
    api.get(`/admin/hifz/suivi/classes/${classeId}`).then(r => r.data),

  getHifzSuiviEleve: (eleveId: number) =>
    api.get(`/admin/hifz/suivi/eleves/${eleveId}`).then(r => r.data),

  // Explications enfants (tafsir simplifie)
  getTafsir: (sourate: number) =>
    api.get(`/admin/hifz/tafsir/${sourate}`).then(r => r.data),

  saveTafsir: (data: { sourate: number; ayah: number; texte: string; source?: string; statut?: 'A_VALIDER' | 'VALIDE' }) =>
    api.put('/admin/hifz/tafsir', data).then(r => r.data),

  deleteTafsir: (id: number) =>
    api.delete(`/admin/hifz/tafsir/${id}`).then(r => r.data),

  // Bibliotheque (Libib)
  getLibibConfig: () =>
    api.get('/admin/libib/config').then(r => r.data),

  setLibibConfig: (data: { apiKey?: string; apiUser?: string; catalogUrl?: string; manageUrl?: string; patronHistoryPrefix?: string }) =>
    api.put('/admin/libib/config', data).then(r => r.data),

  getDossierLibib: (id: number) =>
    api.get(`/admin/dossiers/${id}/libib`).then(r => r.data),

  createLibibPatron: (id: number) =>
    api.post(`/admin/dossiers/${id}/libib/patron`).then(r => r.data),

  setLibibHistoryId: (id: number, historyId: string) =>
    api.put(`/admin/dossiers/${id}/libib/history-id`, { historyId }).then(r => r.data),

  sendLibibCredentials: (id: number, data: { sendEmail?: boolean; sendSms?: boolean }) =>
    api.post(`/admin/dossiers/${id}/libib/send-credentials`, data).then(r => r.data),

  getLibibCredentialsPreview: (id: number) =>
    api.get(`/admin/dossiers/${id}/libib/credentials-preview`).then(r => r.data),

  // Google Drive
  listDriveFiles: (googleAccessToken: string) =>
    api.get('/admin/drive/files', {
      headers: { 'X-Google-Access-Token': googleAccessToken },
    }).then(r => r.data),

  listDriveChequeFiles: (googleAccessToken: string) =>
    api.get('/admin/drive/cheque-files', {
      headers: { 'X-Google-Access-Token': googleAccessToken },
    }).then(r => r.data),

  downloadDriveFile: (fileId: string, googleAccessToken: string) =>
    api.get(`/admin/drive/download/${fileId}`, {
      headers: { 'X-Google-Access-Token': googleAccessToken },
      responseType: 'blob',
    }).then(r => r.data as Blob),

  // ── Répartition: Salles ──
  getSalles: (site?: string) => api.get('/admin/salles', { params: { site } }).then(r => r.data),
  createSalle: (data: Record<string, unknown>) => api.post('/admin/salles', data).then(r => r.data),
  updateSalle: (id: number, data: Record<string, unknown>) => api.put(`/admin/salles/${id}`, data).then(r => r.data),
  deleteSalle: (id: number) => api.delete(`/admin/salles/${id}`).then(r => r.data),

  // ── Répartition: TimeSlots ──
  getTimeSlots: (params?: { site?: string; jour?: string }) => api.get('/admin/timeslots', { params }).then(r => r.data),
  createTimeSlot: (data: Record<string, unknown>) => api.post('/admin/timeslots', data).then(r => r.data),
  updateTimeSlot: (id: number, data: Record<string, unknown>) => api.put(`/admin/timeslots/${id}`, data).then(r => r.data),
  deleteTimeSlot: (id: number) => api.delete(`/admin/timeslots/${id}`).then(r => r.data),

  // ── Répartition: TypeCours ──
  getTypeCours: () => api.get('/admin/typecours').then(r => r.data),
  createTypeCours: (data: Record<string, unknown>) => api.post('/admin/typecours', data).then(r => r.data),
  updateTypeCours: (id: number, data: Record<string, unknown>) => api.put(`/admin/typecours/${id}`, data).then(r => r.data),
  deleteTypeCours: (id: number) => api.delete(`/admin/typecours/${id}`).then(r => r.data),

  // ── Répartition: Manuels ──
  getManuels: () => api.get('/admin/manuels').then(r => r.data),
  createManuel: (data: Record<string, unknown>) => api.post('/admin/manuels', data).then(r => r.data),
  updateManuel: (id: number, data: Record<string, unknown>) => api.put(`/admin/manuels/${id}`, data).then(r => r.data),
  deleteManuel: (id: number) => api.delete(`/admin/manuels/${id}`).then(r => r.data),
  uploadManuelCouverture: (id: number, file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/admin/manuels/${id}/couverture`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  getManuelCouverture: (id: number) =>
    api.get(`/admin/manuels/${id}/couverture`, { responseType: 'blob' }).then(r => URL.createObjectURL(r.data)),

  // ── Répartition: Niveaux ──
  getNiveaux: (typeCoursId?: number) => api.get('/admin/niveaux', { params: typeCoursId ? { typeCoursId } : {} }).then(r => r.data),
  createNiveau: (data: Record<string, unknown>) => api.post('/admin/niveaux', data).then(r => r.data),
  updateNiveau: (id: number, data: Record<string, unknown>) => api.put(`/admin/niveaux/${id}`, data).then(r => r.data),
  deleteNiveau: (id: number) => api.delete(`/admin/niveaux/${id}`).then(r => r.data),

  // ── Répartition: Enseignants ──
  getEnseignants: () => api.get('/admin/enseignants').then(r => r.data),
  getEnseignant: (id: number) => api.get(`/admin/enseignants/${id}`).then(r => r.data),
  createEnseignant: (data: Record<string, unknown>) => api.post('/admin/enseignants', data).then(r => r.data),
  updateEnseignant: (id: number, data: Record<string, unknown>) => api.put(`/admin/enseignants/${id}`, data).then(r => r.data),
  deleteEnseignant: (id: number) => api.delete(`/admin/enseignants/${id}`).then(r => r.data),
  uploadEnseignantCv: (id: number, file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/admin/enseignants/${id}/cv`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  downloadEnseignantCv: (id: number) => api.get(`/admin/enseignants/${id}/cv`, { responseType: 'blob' }).then(r => r.data),

  // ── Défraiements enseignants ──
  getDefraiements: (annee: number, mois: number) =>
    api.get('/admin/defraiements', { params: { annee, mois } }).then(r => r.data),
  overrideDefraiement: (enseignantId: number, annee: number, mois: number, data: { montantOverride: string | null; note?: string }) =>
    api.put(`/admin/defraiements/${enseignantId}`, data, { params: { annee, mois } }).then(r => r.data),
  setDefraiementStatut: (enseignantId: number, annee: number, mois: number, statut: 'EN_ATTENTE' | 'PREPARE' | 'DONNE', paiement?: DefraiementPaiementSaisie) =>
    api.patch(`/admin/defraiements/${enseignantId}/statut`, { statut, ...paiement }, { params: { annee, mois } }).then(r => r.data),
  uploadDefraiementChequeScan: (file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/admin/defraiements/cheque-scan', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data as { filename: string });
  },
  getDefraiementChequeScan: (enseignantId: number, annee: number, mois: number) =>
    api.get(`/admin/defraiements/${enseignantId}/scan`, { params: { annee, mois }, responseType: 'blob' }).then(r => r.data as Blob),
  getPermanentChequeScan: (permanentId: number, annee: number, mois: number) =>
    api.get(`/admin/defraiements/permanents/${permanentId}/scan`, { params: { annee, mois }, responseType: 'blob' }).then(r => r.data as Blob),
  setPermanentStatut: (permanentId: number, annee: number, mois: number, statut: 'EN_ATTENTE' | 'PREPARE' | 'DONNE', paiement?: DefraiementPaiementSaisie) =>
    api.patch(`/admin/defraiements/permanents/${permanentId}/statut`, { statut, ...paiement }, { params: { annee, mois } }).then(r => r.data),
  getPermanentCandidats: () =>
    api.get('/admin/defraiements/permanents/candidats').then(r => r.data as { id: number; name: string; email: string }[]),
  createPermanent: (data: PermanentSaisie & { adminUserId: number }) =>
    api.post('/admin/defraiements/permanents', data).then(r => r.data),
  updatePermanent: (permanentId: number, data: PermanentSaisie) =>
    api.put(`/admin/defraiements/permanents/${permanentId}`, data).then(r => r.data),
  deletePermanent: (permanentId: number) =>
    api.delete(`/admin/defraiements/permanents/${permanentId}`).then(r => r.data as { supprime: boolean }),
  ajusterPermanent: (permanentId: number, annee: number, mois: number, data: { heuresAjustees: string | null; demiJourneesAjustees: string | null; montantOverride: string | null; note?: string }) =>
    api.put(`/admin/defraiements/permanents/${permanentId}/mois`, data, { params: { annee, mois } }).then(r => r.data),
  getDefraiementSeances: (enseignantId: number, annee: number, mois: number) =>
    api.get(`/admin/defraiements/${enseignantId}/seances`, { params: { annee, mois } }).then(r => r.data),
  getDefraiementAbsences: (statut?: string) =>
    api.get('/admin/defraiements/absences', { params: statut ? { statut } : {} }).then(r => r.data),
  getDefraiementCreneaux: (enseignantId: number, date: string) =>
    api.get(`/admin/defraiements/enseignants/${enseignantId}/creneaux`, { params: { date } }).then(r => r.data),
  createDefraiementAbsence: (data: { enseignantId: number; date: string; classeId?: number | null; classeIds?: number[]; motif?: string; remplacantId?: number | null }) =>
    api.post('/admin/defraiements/absences', data).then(r => r.data),
  updateDefraiementAbsence: (id: number, statut: 'VALIDEE' | 'REFUSEE') =>
    api.patch(`/admin/defraiements/absences/${id}`, { statut }).then(r => r.data),
  editDefraiementAbsence: (id: number, data: { date?: string; classeId?: number | null; motif?: string | null; remplacantId?: number | null; statut?: 'DECLAREE' | 'VALIDEE' | 'REFUSEE' }) =>
    api.patch(`/admin/defraiements/absences/${id}`, data).then(r => r.data),
  deleteDefraiementAbsence: (id: number) =>
    api.delete(`/admin/defraiements/absences/${id}`).then(r => r.data),


  // ── Répartition: Classes ──
  getClasses: (params?: { site?: string; typeCours?: string; statut?: string }) =>
    api.get('/admin/classes', { params }).then(r => r.data),

  // ── Planning ──
  getPlanning: (params: { site: string; annee: string }) =>
    api.get('/admin/planning', { params }).then(r => r.data),
  getClasse: (id: number) => api.get(`/admin/classes/${id}`).then(r => r.data),
  createClasse: (data: Record<string, unknown>) => api.post('/admin/classes', data).then(r => r.data),
  updateClasse: (id: number, data: Record<string, unknown>) => api.put(`/admin/classes/${id}`, data).then(r => r.data),
  getClasseDonneesLiees: (id: number) => api.get(`/admin/classes/${id}/donnees-liees`).then(r => r.data),
  getClasseVie: (id: number) => api.get(`/admin/classes/${id}/vie`).then(r => r.data),
  nettoyerClasseDonneesLiees: (id: number) => api.delete(`/admin/classes/${id}/donnees-liees`).then(r => r.data),
  deleteClasse: (id: number) => api.delete(`/admin/classes/${id}`).then(r => r.data),
  changeStatutClasse: (id: number, statut: string) => api.put(`/admin/classes/${id}/statut`, { statut }).then(r => r.data),
  getClasseEleves: (id: number) => api.get(`/admin/classes/${id}/eleves`).then(r => r.data),
  getClasseCandidats: (id: number, allParcours?: boolean) => api.get(`/admin/classes/${id}/candidats`, { params: { allParcours } }).then(r => r.data),
  affecterEleve: (classeId: number, eleveId: number, force?: boolean, forceCapacite?: boolean, replaceSameType?: boolean) =>
    api.post(`/admin/classes/${classeId}/eleves/${eleveId}`, { force: !!force, forceCapacite: !!forceCapacite, replaceSameType: !!replaceSameType }).then(r => r.data),
  retirerEleve: (classeId: number, eleveId: number) =>
    api.delete(`/admin/classes/${classeId}/eleves/${eleveId}`).then(r => r.data),
  getClasseAppel: (classeId: number, date?: string) =>
    api.get(`/admin/classes/${classeId}/appel`, { params: date ? { date } : {} }).then(r => r.data),
  saveClasseAppel: (classeId: number, payload: Record<string, unknown>) =>
    api.post(`/admin/classes/${classeId}/appel`, payload).then(r => r.data),

  // ── Liste des affectations (vue Excel) ──
  getAffectationsListe: (params?: { site?: string; typeCours?: string; jour?: string }) =>
    api.get('/admin/affectations/liste', { params }).then(r => r.data),

  // ── Répartition: Elèves ──
  getElevesRepartition: (params?: { site?: string; parcours?: string; affecte?: boolean; search?: string }) =>
    api.get('/admin/repartition/eleves', { params }).then(r => r.data),
  getPreviousYearData: () =>
    api.get('/admin/repartition/previous-year-data').then(r => r.data),
  setNiveauRecommande: (eleveId: number, typeCoursCode: string, niveauId: number | null) =>
    api.put(`/admin/eleves/${eleveId}/niveau-recommande`, { typeCoursCode, niveauId }).then(r => r.data),

  // ── Duplicate detection & merge ──
  getDuplicates: (dossierId: number) =>
    api.get(`/admin/dossiers/${dossierId}/duplicates`).then(r => r.data),

  compareFamilies: (targetId: number, sourceId: number) =>
    api.get(`/admin/dossiers/${targetId}/compare/${sourceId}`).then(r => r.data),

  mergeFamilies: (targetId: number, sourceId: number, fieldChoices: Record<string, string>) =>
    api.post(`/admin/dossiers/${targetId}/merge/${sourceId}`, fieldChoices).then(r => r.data),

  ignoreDuplicate: (dossierId: number, otherId: number) =>
    api.post(`/admin/dossiers/${dossierId}/duplicates/${otherId}/ignore`).then(r => r.data),

  unignoreDuplicate: (dossierId: number, otherId: number) =>
    api.delete(`/admin/dossiers/${dossierId}/duplicates/${otherId}/ignore`).then(r => r.data),

  // ── Inscription Links ──
  getInscriptionLinks: () =>
    api.get('/admin/inscription-links').then(r => r.data),

  generateInscriptionLink: () =>
    api.post('/admin/inscription-links').then(r => r.data),

  revokeInscriptionLink: (id: number) =>
    api.delete(`/admin/inscription-links/${id}`).then(r => r.data),

  // ── AI Assistant ──
  getAiStatus: () =>
    api.get('/admin/ai/status').then(r => r.data),

  aiChat: (messages: Array<{ role: string; content: string }>, conversationId?: number) =>
    api.post('/admin/ai/chat', { messages, conversationId }).then(r => r.data),

  getAiConversations: () =>
    api.get('/admin/ai/conversations').then(r => r.data),

  getAiConversation: (id: number) =>
    api.get(`/admin/ai/conversations/${id}`).then(r => r.data),

  deleteAiConversation: (id: number) =>
    api.delete(`/admin/ai/conversations/${id}`).then(r => r.data),

  renameAiConversation: (id: number, title: string) =>
    api.patch(`/admin/ai/conversations/${id}`, { title }).then(r => r.data),

  // ── Calendrier Scolaire ──
  getCalendrier: (start: string, end: string, courseDays?: string[]) =>
    api.get('/admin/calendrier', {
      params: { start, end, ...(courseDays?.length ? { courseDays } : {}) },
      paramsSerializer: { indexes: null },
    }).then(r => r.data),

  getDatesCles: () =>
    api.get('/admin/dates-cles').then(r => r.data),

  createDateCle: (data: { label: string; description?: string; dateDebut: string; dateFin?: string; type: string; ordre?: number; site?: string; heureDebut?: string; heureFin?: string; coursMaintenu?: boolean | null; visibilite?: string; liens?: { label?: string; url: string }[] }) =>
    api.post('/admin/dates-cles', data).then(r => r.data),

  updateDateCle: (id: number, data: { label: string; description?: string; dateDebut: string; dateFin?: string; type: string; ordre?: number; site?: string; heureDebut?: string; heureFin?: string; coursMaintenu?: boolean | null; visibilite?: string; liens?: { label?: string; url: string }[] }) =>
    api.put(`/admin/dates-cles/${id}`, data).then(r => r.data),

  deleteDateCle: (id: number) =>
    api.delete(`/admin/dates-cles/${id}`).then(r => r.data),

  uploadDateCleImage: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/admin/dates-cles/${id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  deleteDateCleImage: (id: number, filename: string) =>
    api.delete(`/admin/dates-cles/${id}/images/${filename}`).then(r => r.data),
  getDateCleImage: (id: number, filename: string) =>
    api.get(`/admin/dates-cles/${id}/images/${filename}`, { responseType: 'blob' }).then(r => URL.createObjectURL(r.data)),

  // ── Annonces ──
  getAnnonces: () =>
    api.get('/admin/annonces').then(r => r.data),

  createAnnonce: (data: { titre: string; resume: string; type: string; audience: string; datePublication?: string; dateExpiration?: string; active?: boolean }) =>
    api.post('/admin/annonces', data).then(r => r.data),

  updateAnnonce: (id: number, data: { titre: string; resume: string; type: string; audience: string; datePublication?: string; dateExpiration?: string; active?: boolean }) =>
    api.put(`/admin/annonces/${id}`, data).then(r => r.data),

  deleteAnnonce: (id: number) =>
    api.delete(`/admin/annonces/${id}`).then(r => r.data),

  uploadAnnonceImage: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/admin/annonces/${id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  deleteAnnonceImage: (id: number, filename: string) =>
    api.delete(`/admin/annonces/${id}/images/${filename}`).then(r => r.data),
  getAnnonceImage: (id: number, filename: string) =>
    api.get(`/admin/annonces/${id}/images/${filename}`, { responseType: 'blob' }).then(r => URL.createObjectURL(r.data)),

  // ── Compétences ──
  getCompetences: () =>
    api.get('/admin/competences').then(r => r.data),

  createCompetence: (data: { typeCoursId: number; niveauId: number; libelle: string; description?: string; ordre?: number }) =>
    api.post('/admin/competences', data).then(r => r.data),

  updateCompetence: (id: number, data: { typeCoursId: number; niveauId: number; libelle: string; description?: string; ordre?: number }) =>
    api.put(`/admin/competences/${id}`, data).then(r => r.data),

  deleteCompetence: (id: number) =>
    api.delete(`/admin/competences/${id}`).then(r => r.data),

  // ── Documents partagés ──
  getDocuments: () =>
    api.get('/admin/documents').then(r => r.data),

  uploadDocument: (formData: FormData) =>
    api.post('/admin/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),

  deleteDocument: (id: number) =>
    api.delete(`/admin/documents/${id}`).then(r => r.data),

  downloadDocument: (id: number) =>
    api.get(`/admin/documents/${id}/download`, { responseType: 'blob' }).then(r => r.data as Blob),

  // ── Engagements (documents à signer par les enseignants) ──
  getEngagements: () =>
    api.get('/admin/engagements').then(r => r.data),

  uploadEngagement: (formData: FormData) =>
    api.post('/admin/engagements', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),

  updateEngagement: (id: number, data: { titre?: string; description?: string; obligatoire?: boolean; actif?: boolean; ordre?: number }) =>
    api.put(`/admin/engagements/${id}`, data).then(r => r.data),

  deleteEngagement: (id: number) =>
    api.delete(`/admin/engagements/${id}`).then(r => r.data),

  downloadEngagement: (id: number) =>
    api.get(`/admin/engagements/${id}/download`, { responseType: 'blob' }).then(r => r.data as Blob),

  getEngagementSignatures: (id: number) =>
    api.get(`/admin/engagements/${id}/signatures`).then(r => r.data),

  getEngagementSuivi: () =>
    api.get('/admin/engagements/suivi').then(r => r.data),

  // ── Travaux à faire (supervision) ──
  getDevoirs: (params?: { enseignantId?: number; timeSlotId?: number; classeId?: number; typeCoursId?: number; niveau?: string; site?: string; statut?: string; from?: string; to?: string }) =>
    api.get('/admin/devoirs', { params }).then(r => r.data),

  getDevoirsStats: () =>
    api.get('/admin/devoirs/stats').then(r => r.data),

  getDevoirDetail: (id: number) =>
    api.get(`/admin/devoirs/${id}`).then(r => r.data),

  updateDevoir: (id: number, body: { titre?: string; description?: string }) =>
    api.put(`/admin/devoirs/${id}`, body).then(r => r.data),

  publierDevoir: (id: number) =>
    api.post(`/admin/devoirs/${id}/publier`).then(r => r.data),

  deleteDevoir: (id: number) =>
    api.delete(`/admin/devoirs/${id}`).then(r => r.data),

  // ── Vie scolaire : Classes & seances ──
  getVieScolaireClasses: (params?: { site?: string; typeCours?: string }) =>
    api.get('/admin/vie-scolaire/classes', { params }).then(r => r.data),

  getVieScolaireClasse: (id: number) =>
    api.get(`/admin/vie-scolaire/classes/${id}/vie`).then(r => r.data),

  getVieScolaireSeance: (classeId: number, date: string) =>
    api.get(`/admin/vie-scolaire/classes/${classeId}/seances/${date}`).then(r => r.data),

  getVieScolaireDevoirs: (classeId: number) =>
    api.get(`/admin/vie-scolaire/classes/${classeId}/devoirs`).then(r => r.data),

  getVieScolaireSeancesPlanning: (classeId: number) =>
    api.get(`/admin/vie-scolaire/classes/${classeId}/seances-planning`).then(r => r.data),

  getVieScolaireAppel: (classeId: number, date: string) =>
    api.get(`/admin/vie-scolaire/classes/${classeId}/appel`, { params: { date } }).then(r => r.data),

  saisirVieScolaireAppel: (classeId: number, body: { date: string; presences: Array<{ eleveId: number; statut: string; commentaire?: string; heureArrivee?: string | null }> }) =>
    api.post(`/admin/vie-scolaire/classes/${classeId}/appel`, body).then(r => r.data),

  saisirPresenceEleve: (eleveId: number, body: { classeId: number; date: string; statut: string; commentaire?: string }) =>
    api.post(`/admin/vie-scolaire/eleves/${eleveId}/presence`, body).then(r => r.data),

  // ── Vie scolaire : Gestion des absences ──
  getAbsencesPrioritaires: (params?: { classeId?: number; jour?: string }) =>
    api.get('/admin/vie-scolaire/absences/prioritaires', { params }).then(r => r.data),

  getAbsencesHistorique: (params?: { classeId?: number }) =>
    api.get('/admin/vie-scolaire/absences/historique', { params }).then(r => r.data),

  getClassesSansAppel: () =>
    api.get('/admin/vie-scolaire/absences/classes-sans-appel').then(r => r.data),

  getAbsencesListe: (params?: {
    classeId?: number; jour?: string; timeSlotId?: number; statut?: string;
    from?: string; to?: string; q?: string; groupBy?: 'mois' | 'aucun'; page?: number; size?: number;
  }) =>
    api.get('/admin/vie-scolaire/absences/liste', { params }).then(r => r.data),

  traiterAbsence: (body: { eleveId: number; classeId: number; statut: string; commentaire?: string }) =>
    api.post('/admin/vie-scolaire/absences/traiter', body).then(r => r.data),

  getAppelsManquants: (params?: { debut?: string; fin?: string; site?: string }) =>
    api.get('/admin/vie-scolaire/absences/appels-manquants', { params }).then(r => r.data),

  // ── Aujourd'hui (accueil permanence) ──
  getAujourdhui: (params?: { site?: string; date?: string }) =>
    api.get('/admin/aujourdhui', { params }).then(r => r.data),

  getAujourdhuiEleves: (params?: { site?: string; date?: string }) =>
    api.get('/admin/aujourdhui/eleves', { params }).then(r => r.data),

  getAujourdhuiFiche: (eleveId: number, date?: string) =>
    api.get(`/admin/aujourdhui/eleves/${eleveId}/fiche`, { params: { date } }).then(r => r.data),

  rechercherElevesAujourdhui: (q: string) =>
    api.get('/admin/aujourdhui/eleves/recherche', { params: { q } }).then(r => r.data),

  getAlertesEleves: () =>
    api.get('/admin/aujourdhui/alertes').then(r => r.data),

  creerAlerteEleve: (eleveId: number, body: AlerteEleveInput) =>
    api.post(`/admin/aujourdhui/eleves/${eleveId}/alertes`, body).then(r => r.data),

  modifierAlerteEleve: (id: number, body: AlerteEleveInput) =>
    api.put(`/admin/aujourdhui/alertes/${id}`, body).then(r => r.data),

  desactiverAlerteEleve: (id: number) =>
    api.delete(`/admin/aujourdhui/alertes/${id}`).then(r => r.data),

  // ── Tâches ──
  getMesTaches: () =>
    api.get('/admin/taches/mes-taches').then(r => r.data),

  getTaches: () =>
    api.get('/admin/taches').then(r => r.data),

  getTacheDestinataires: () =>
    api.get('/admin/taches/destinataires').then(r => r.data),

  creerTache: (body: TacheInput) =>
    api.post('/admin/taches', body).then(r => r.data),

  modifierTache: (id: number, body: TacheInput) =>
    api.put(`/admin/taches/${id}`, body).then(r => r.data),

  supprimerTache: (id: number) =>
    api.delete(`/admin/taches/${id}`).then(r => r.data),

  marquerTacheFaite: (id: number, commentaire?: string) =>
    api.post(`/admin/taches/${id}/fait`, { commentaire }).then(r => r.data),

  annulerTacheFaite: (id: number) =>
    api.delete(`/admin/taches/${id}/fait`).then(r => r.data),
};

export interface AlerteEleveInput {
  categorie: string;
  niveau: string;
  titre: string;
  consigne?: string;
  debut?: string;
  fin?: string;
}

export interface TacheInput {
  titre: string;
  procedure?: string;
  priorite: string;
  echeance?: string;
  portee: 'TOUS' | 'CIBLEE';
  modeRealisation: 'INDIVIDUELLE' | 'PARTAGEE';
  utilisateurIds?: number[];
  profilIds?: number[];
}

export type ModePaiementDefraiement = 'ESPECES' | 'CHEQUE' | 'VIREMENT' | 'ABANDON';

export interface DefraiementPaiementSaisie {
  modePaiement: ModePaiementDefraiement;
  chequeNom?: string;
  chequeNumero?: string;
  chequeBanque?: string;
  scanChequePath?: string;
}

export interface CreneauPermanenceInput {
  jour: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
  unite: 'HEURE' | 'DEMI_JOURNEE';
  quantite: string;
  dateDebut?: string | null;
  dateFin?: string | null;
}

export interface PermanentSaisie {
  tauxHoraire?: string | null;
  tauxDemiJournee?: string | null;
  site?: string | null;
  note?: string | null;
  actif?: boolean;
  creneaux: CreneauPermanenceInput[];
}

/* ── Auth API ──────────────────────────────────────────────────────────────── */

export const authApi = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),

  googleLogin: (idToken: string) =>
    api.post('/auth/google', { idToken }),

  logout: () =>
    api.post('/auth/logout').then(() => { setAccessToken(null); }),

  refresh: () =>
    api.post('/auth/refresh').then(r => r.data),
};
